import { useEffect } from 'react';

/**
 * Tiene lo schermo acceso finche' serve, in una forma sola per tutta l'app.
 *
 * Nasce per l'orologio dal vivo — due giocatori su una damiera vera, il telefono
 * appoggiato accanto, e nessun tocco per minuti interi — ma il problema non e'
 * dell'orologio: e' di ogni superficie che si GUARDA senza toccarla. Una partita
 * mentre l'avversario pensa, un problema su cui si ragiona, l'attesa del turno
 * di un torneo. Con lo spegnimento automatico del sistema, tutte quelle cose
 * diventano cose che non si vedono.
 *
 * Tre cose che questo hook deve fare e che si scordano facilmente:
 *
 * 1. **Il lucchetto si perde da se'.** Quando la pagina va in secondo piano
 *    (o lo schermo si spegne comunque, per esempio col tasto di blocco) il
 *    browser lo rilascia, e tornando non si riaccende da solo: va richiesto di
 *    nuovo su `visibilitychange`. Senza questo, l'attrezzo funziona per la prima
 *    partita e non per la seconda.
 * 2. **Non c'e' su tutti i browser.** Firefox non ha `navigator.wakeLock`, e i
 *    tipi del DOM lo dichiarano come SEMPRE presente: e' il punto esatto in cui
 *    il compilatore non aiuta, quindi deve aiutare un test.
 * 3. **La richiesta puo' essere RIFIUTATA** anche dove l'API esiste — batteria
 *    bassa, risparmio energetico, pagina non visibile nell'istante della
 *    richiesta. La promessa va sempre con il suo `catch`.
 *
 * ---
 *
 * UN CONTATORE DI MODULO, e la ragione NON e' quella che sembra.
 *
 * Verrebbe da dire che sentinelle separate si pestino: due superfici insieme, la
 * prima si smonta, rilascia, e lo schermo si spegne sotto la seconda. **Non e'
 * cosi'**: il lucchetto del documento resta preso finche' una qualunque delle
 * sue sentinelle e' viva, quindi sette chiamate indipendenti sarebbero gia'
 * corrette. Qui non si sta riparando un difetto.
 *
 * La ragione e' l'ascoltatore. Con una sentinella per chiamata, ogni superficie
 * monta il SUO `visibilitychange`: sette superfici fanno sette ascoltatori e
 * sette richieste al browser a ogni ritorno in primo piano, per tenere acceso un
 * solo schermo. La regola che lo vieta e' gia' scritta per la domanda gemella,
 * in `usePaginaNascosta`: «gli ascoltatori si registrano una volta sola a
 * livello di modulo — la domanda e' globale, e ogni componente che la fa non
 * deve aggiungerne di propri».
 *
 * Quindi: contatore di modulo, UNA sentinella, UN ascoltatore. E' la forma di
 * `useBloccoLente`, con una differenza — li' il contatore segue il MONTAGGIO e
 * l'hook non prende argomenti; qui segue una CONDIZIONE, perche' alcune delle
 * superfici che chiamano (il banner della gara, quello del tavolo) sono montate
 * sempre e si spengono con un ritorno anticipato.
 */

let richieste = 0;
let sentinella: WakeLockSentinel | null = null;
/** La richiesta in volo: senza, due incrementi ravvicinati ne chiedono due. */
let inVolo = false;

function serve(): boolean {
  return richieste > 0;
}

function veglia(): WakeLock | undefined {
  /*
   * I tipi del DOM dichiarano `navigator.wakeLock` come sempre presente, e a
   * runtime non lo e'. L'annotazione `| undefined` serve solo a rendere lecita
   * la guardia di chi chiama.
   */
  if (typeof navigator === 'undefined') return undefined;
  const w: WakeLock | undefined = navigator.wakeLock;
  return w;
}

/**
 * Porta il lucchetto in pari con il contatore.
 *
 * Idempotente di proposito: la chiamano l'hook a ogni cambio e l'ascoltatore di
 * visibilita' a ogni ritorno in primo piano, e deve poter girare a vuoto.
 */
function allinea(): void {
  const api = veglia();
  if (!api) return;

  if (!serve()) {
    const s = sentinella;
    sentinella = null;
    if (s && !s.released) void s.release().catch(() => {});
    return;
  }

  if (inVolo) return;
  if (sentinella && !sentinella.released) return;
  /*
   * Niente richiesta a pagina nascosta: il browser la rifiuta e lascia a terra
   * una promessa respinta. Al ritorno in primo piano ci pensa l'ascoltatore qui
   * sotto, che e' lo stesso che copre la perdita del lucchetto.
   *
   * ⚠️ Si legge `document.visibilityState`, e NON `paginaNascosta()`.
   * Sembrano la stessa domanda e non lo sono: quella scrive `true` su
   * `pagehide` SENZA rileggere `visibilityState`, apposta per il caso iOS in cui
   * la pagina viene congelata senza eventi. Usarla come guardia la farebbe
   * auto-annullare — e' il difetto gia' visto nel ripiego di `useArenaQueueGuard`.
   * La regola: `paginaNascosta()` puo' riarmare, `document.visibilityState`
   * decide.
   */
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;

  inVolo = true;
  api.request('screen').then(
    (s) => {
      inVolo = false;
      /* Nel frattempo l'ultima superficie puo' essersi spenta: in quel caso il
         lucchetto appena ottenuto si molla subito, o resterebbe preso da
         nessuno. */
      if (!serve()) { void s.release().catch(() => {}); return; }
      sentinella = s;
    },
    () => {
      inVolo = false;
      /* Rifiutata: si continua senza, e lo schermo si spegnera' come sempre. */
    },
  );
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', allinea);
}

/** Tiene acceso lo schermo finche' `attivo` e' vero. */
export function useSchermoSveglio(attivo: boolean): void {
  useEffect(() => {
    if (!attivo) return;
    richieste += 1;
    allinea();
    return () => {
      richieste -= 1;
      allinea();
    };
  }, [attivo]);
}

/** Solo per i test: riporta il contatore a zero fra un caso e l'altro. */
export function azzeraSchermoSveglio(): void {
  richieste = 0;
  inVolo = false;
  const s = sentinella;
  sentinella = null;
  if (s && !s.released) void s.release().catch(() => {});
}
